import { type ReactNode, useState } from 'react';

import Img from 'components/Img';
import ListItem from 'components/ListItem';
import Popover from 'components/Popover';
import Price from 'components/Price';
import SelectButton from 'components/SelectButton';
import type { VariantStockResponse } from 'models/api';
import type { ProductPrice } from 'models/price';

import StockInformation from './StockInformation';

interface BaseVariant {
	imageAlt?: string;
	imageSrc?: string;
	isSelected: boolean;
	key: string;
	price?: ProductPrice;
	title: string | undefined;
	url: string;
}

interface VariantWithoutStock extends BaseVariant {
	isSellable?: never;
	isSellableOnWeb?: never;
	variantId?: never;
}

interface VariantWithStock extends BaseVariant {
	isSellable: boolean;
	isSellableOnWeb: boolean;
	variantId: string;
}

interface BaseProps {
	buttonLabel: string;
	buttonSupportingAction?: ReactNode;
	buttonSupportingText?: string;
	className?: string;
	onOpen?: () => void;
	popoverStartContent?: ReactNode;
	popoverTitle: string;
	selectedImageUrl?: string;
	selectedText: string | undefined;
	shouldScrollToTopOnSelect?: boolean;
}

interface PropsWithoutStock extends BaseProps {
	stockData?: never;
	stockIsLoading?: never;
	variants: VariantWithoutStock[];
}

interface PropsWithStock extends BaseProps {
	stockData: VariantStockResponse[] | undefined;
	stockIsLoading: boolean;
	variants: VariantWithStock[];
}

type Props = PropsWithoutStock | PropsWithStock;

export default function BasePicker({
	buttonLabel,
	buttonSupportingAction,
	buttonSupportingText,
	className,
	onOpen,
	popoverStartContent,
	popoverTitle,
	selectedImageUrl,
	selectedText,
	shouldScrollToTopOnSelect,
	stockData,
	stockIsLoading = false,
	variants,
}: Props) {
	const [isOpen, setIsOpen] = useState(false);

	if (variants.length < 2) {
		return null;
	}

	return (
		<>
			<SelectButton
				label={buttonLabel}
				className={className}
				onClick={() => {
					onOpen?.();
					setIsOpen(true);
				}}
				aria-haspopup="dialog"
				text={selectedText || '—'}
				imageUrl={selectedImageUrl}
				supportingAction={buttonSupportingAction}
				supportingText={buttonSupportingText}
			/>
			<Popover
				isOpen={isOpen}
				title={popoverTitle}
				onClose={() => {
					setIsOpen(false);
				}}
			>
				{popoverStartContent}
				<ul className="space-y-2">
					{variants.map((variant: VariantWithoutStock | VariantWithStock) => (
						<ListItem
							key={variant.key}
							as="li"
							title={variant.title || '—'}
							href={variant.url}
							shouldScrollToTop={shouldScrollToTopOnSelect}
							// Center if there is no stock information shown.
							alignment={variant.variantId ? undefined : 'center'}
							startContent={
								variant.imageSrc ? (
									<Img
										src={variant.imageSrc}
										alt={variant.imageAlt}
										className="size-10 shrink-0"
										width={40}
										height={40}
									/>
								) : null
							}
							endContent={
								variant.price ? <Price price={variant.price} size="xs" /> : null
							}
							isSelected={variant.isSelected}
						>
							{variant.variantId && (
								<StockInformation
									stock={stockData?.find(
										(variantStock) =>
											variantStock.variantId === variant.variantId,
									)}
									isLoading={stockIsLoading}
								/>
							)}
						</ListItem>
					))}
				</ul>
			</Popover>
		</>
	);
}
BasePicker.displayName = 'ProductDetails_BasePicker';
